if (!customElements.get('custom-product-configurator')) {
  class CustomProductConfigurator extends HTMLElement {
    constructor() {
      super();
      const { variants, selectedVariantId, productTitle, setupFeeProduct = {}, cartItems = [], productId } = JSON.parse(this.querySelector('.custom-product-configurator__data').textContent);
      if (!variants) return;
      this.variants = variants;
      this.setupFeeProduct = setupFeeProduct;
      this.productId = productId;
      // The product form's [name="id"] is what actually gets ordered, so it is the source of truth.
      // It can already differ from the server-rendered selectedVariantId if the customer changed
      // thickness before this script loaded.
      const selectedVariant = this.findVariant(this.getFormVariantId())
        ?? this.findVariant(selectedVariantId)
        ?? variants[0];
      this.addSetupFeeProduct = true;
      this.cartItems = cartItems;
      this.selectedVariantId = selectedVariant.id;
      this.selects = {};
      this.productTitle = productTitle;
      this.activeLengthEntry = null;
      this.activeWidthEntry = null;
      this.totalProductPrice = 0;
      this.quantity = 1;
      this.isSubmitting = false;
      this.initCustomSelectListener();
      this.updateBtnPrice();
      this.initQuantityPriceUpdater();
      this.initSubmitFormListeners();
      this.initSubmitGuard();

      // Listen from construction rather than after the lazy custom-select init, so a thickness
      // change made before the Length/Width dropdowns initialise is not missed.
      this.closest('.js-product')?.addEventListener('on:variant:change', () => {
        this.syncVariantFromForm();
      });

      document.addEventListener("on:cart:add", () => {
        this.resetSelects();
      });
    }

    findVariant(id) {
      return this.variants.find(v => v.id === Number(id));
    }

    getFormVariantId() {
      const input = document.querySelector(`buy-buttons form[data-product-id='${this.productId}'] [name="id"]`);
      return input ? input.value : null;
    }

    /**
     * Brings the configurator in line with the variant in the product form.
     * @returns {boolean} True if the selected variant changed.
     */
    syncVariantFromForm() {
      const variant = this.findVariant(this.getFormVariantId());
      if (!variant || variant.id === this.selectedVariantId) return false;

      this.selectedVariantId = variant.id;
      Object.entries(this.selects).forEach(([type, { instance, reinit }]) => {
        this.initSelect(instance, reinit, type);
      });
      this.handleVariantChange();
      return true;
    }

    /**
     * Last line of defence: if the configurator and the product form disagree on the variant at
     * submit time, resync and stop the add so the customer sees the correct price before adding.
     * Runs in the capture phase so it fires before the theme's product-form submit handlers.
     */
    initSubmitGuard() {
      document.addEventListener('submit', (evt) => {
        const form = evt.target;
        if (!form.matches?.(`form.js-product-form[data-product-id='${this.productId}']`)) return;
        if (!this.syncVariantFromForm()) return;

        evt.preventDefault();
        evt.stopImmediatePropagation();
      }, true);
    }

    resetSelects() {
      if (!this.isSubmitting) return;
      this.isSubmitting = false;

      this.activeLengthEntry = null;
      this.activeWidthEntry = null;

      const lengthSelect = document.querySelector('#custom-product-configurator-length');
      const widthSelect = document.querySelector('#custom-product-configurator-width');

      if (lengthSelect && lengthSelect.selectOption) {
        const firstLengthOption = lengthSelect.querySelector('li[role="option"]');
        lengthSelect.selectOption(firstLengthOption);
      }

      if (widthSelect && widthSelect.selectOption) {
        const firstWidthOption = widthSelect.querySelector('li[role="option"]');
        widthSelect.selectOption(firstWidthOption);
      }

      this.handleVariantChange();
      this.resetQuantityInput();
    }

    initSubmitFormListeners() {
      const addToCartForms = document.querySelectorAll("form.js-product-form");

      addToCartForms.forEach(form => form.addEventListener("submit", () => {
        this.isSubmitting = true;
        this.cartItems.push({ id: this.selectedVariantId, uniqueKey: this.getSelectedVariantUniqKey() });
      }))
    }

    setAddSetupFeeProduct() {
      const selectedVariantUniqKey = this.getSelectedVariantUniqKey();
      const cartHasSelectedSetupFee = this.cartItems.some(
        item => item.uniqueKey === selectedVariantUniqKey
      );
      this.addSetupFeeProduct = !cartHasSelectedSetupFee;
    }

    updateFormsWithProperties() {
      const forms = document.querySelectorAll(`form[data-product-id='${this.productId}']`);
      const activeLengthEntryPrice = this.activeLengthEntry && this.activeLengthEntry[1] ? this.activeLengthEntry[1] : 0;
      const activeWidthEntryPrice = this.activeWidthEntry && this.activeWidthEntry[1] ? this.activeWidthEntry[1] : 0;

      forms?.forEach(form => {
        let _selectionTitleInput = form.querySelector('input[name="properties[_selectionTitle]"]');
        if (!_selectionTitleInput) {
          _selectionTitleInput = document.createElement("input");
          _selectionTitleInput.type = "hidden";
          _selectionTitleInput.name = "properties[_selectionTitle]";
          form.appendChild(_selectionTitleInput);
        }

        let _selectionUniqueKeyInput = form.querySelector('input[name="properties[_selectionUniqueKey]"]');
        if (!_selectionUniqueKeyInput) {
          _selectionUniqueKeyInput = document.createElement("input");
          _selectionUniqueKeyInput.type = "hidden";
          _selectionUniqueKeyInput.name = "properties[_selectionUniqueKey]";
          form.appendChild(_selectionUniqueKeyInput);
        }

        let _selectionPriceInput = form.querySelector('input[name="properties[_selectionPrice]"]');
        if (!_selectionPriceInput) {
          _selectionPriceInput = document.createElement("input");
          _selectionPriceInput.type = "hidden";
          _selectionPriceInput.name = "properties[_selectionPrice]";
          form.appendChild(_selectionPriceInput);
        }

        let _setupFeeItemIdInput = form.querySelector('input[name="properties[_setupFeeItemId]"]');
        if (!_setupFeeItemIdInput) {
          _setupFeeItemIdInput = document.createElement("input");
          _setupFeeItemIdInput.type = "hidden";
          _setupFeeItemIdInput.name = "properties[_setupFeeItemId]";
          form.appendChild(_setupFeeItemIdInput);
        }

        // Add Length in Inches property (visible)
        let lengthInchesInput = form.querySelector('input[name="properties[Length in Inches]"]');
        if (!lengthInchesInput) {
          lengthInchesInput = document.createElement("input");
          lengthInchesInput.type = "text";
          lengthInchesInput.name = "properties[Length in Inches]";
          lengthInchesInput.readOnly = true;
          lengthInchesInput.style.display = "none"; // Hidden but not type="hidden" so it's still a visible property
          form.appendChild(lengthInchesInput);
        }

        // Add Width in Inches property (visible)
        let widthInchesInput = form.querySelector('input[name="properties[Width in Inches]"]');
        if (!widthInchesInput) {
          widthInchesInput = document.createElement("input");
          widthInchesInput.type = "text";
          widthInchesInput.name = "properties[Width in Inches]";
          widthInchesInput.readOnly = true;
          widthInchesInput.style.display = "none"; // Hidden but not type="hidden" so it's still a visible property
          form.appendChild(widthInchesInput);
        }

        _setupFeeItemIdInput.value = this.setupFeeProduct.id;
        _selectionTitleInput.value = this.getFilteredTitle();
        _selectionPriceInput.value = (activeLengthEntryPrice + activeWidthEntryPrice).toFixed(2);
        _selectionUniqueKeyInput.value = this.getSelectedVariantUniqKey();
        
        // Set the Length and Width values
        lengthInchesInput.value = this.activeLengthEntry?.[0] || '';
        widthInchesInput.value = this.activeWidthEntry?.[0] || '';
      });
    }

    getSelectedVariantUniqKey() {
      return `${this.selectedVariantId}-${this.activeWidthEntry?.[0]}-${this.activeLengthEntry?.[0]}`
    }

    getFilteredTitle() {
      const selectedVariant = this.variants.find(v => v.id === +this.selectedVariantId);
      const filteredTitle = `${selectedVariant.title} x ${this.activeWidthEntry?.[0] ?? 0}" x ${this.activeLengthEntry?.[0] ?? 0}" ${this.productTitle}`;
      return filteredTitle;
    }

    resetQuantityInput() {
      const quantityWrapper = document.querySelector('quantity-wrapper');
      if (!quantityWrapper) return;
      const quantityInput = quantityWrapper.querySelector('input[name="quantity"]');
      quantityInput.value = 1;
      this.quantity = 1;
    }

    initQuantityPriceUpdater() {
      const quantityWrapper = document.querySelector('quantity-wrapper');
      if (!quantityWrapper) return;
      const quantityInput = quantityWrapper.querySelector('input[name="quantity"]');
      quantityInput.addEventListener('change', () => {
        this.quantity = parseInt(quantityInput.value) || 1;
        this.updateBtnPrice();
      });
    }

    updateBtnPrice() {
      const atcBtns = document.querySelectorAll(`form[data-product-id='${this.productId}'] .add-to-cart`);
      if (!atcBtns) return;
      const activeWidthEntry = this.activeWidthEntry;
      const activeLengthEntry = this.activeLengthEntry;
      let price = this.totalProductPrice * this.quantity;
      if (this.addSetupFeeProduct) {
        price += this.setupFeeProduct.price;
      }

      atcBtns.forEach(atcBtn => {
        const productForm = atcBtn.closest("product-form");

        if (productForm) {
          atcBtn.textContent = `$${price.toFixed(2)} | Add to cart`;
        }

        if (!activeWidthEntry?.[0] || !activeLengthEntry?.[0]) {
          atcBtn.classList.add("add-to-cart--disabled");
          atcBtn.disabled = true;
        } else {
          atcBtn.classList.remove("add-to-cart--disabled");
          atcBtn.disabled = false;
        }
      })
    }

    handleVariantChange() {
      this.setAddSetupFeeProduct();

      const activeLengthEntryPrice = this.activeLengthEntry && this.activeLengthEntry[1] ? this.activeLengthEntry[1] : 0;
      const activeWidthEntryPrice = this.activeWidthEntry && this.activeWidthEntry[1] ? this.activeWidthEntry[1] : 0;
      this.totalProductPrice = activeLengthEntryPrice + activeWidthEntryPrice;

      this.updateBtnPrice();
      this.updateFormsWithProperties();
      this.updateOptionsVisibility();
    }

    updateOptionsVisibility() {
      const lengthSelect = document.querySelector("#custom-product-configurator-length");
      const widthSelect = document.querySelector("#custom-product-configurator-width");
      if (!lengthSelect || !widthSelect) return;

      const lengthOptions = lengthSelect.querySelectorAll("ul li");
      const widthOptions = widthSelect.querySelectorAll("ul li");

      const lengthValue = +this.activeLengthEntry?.[0] || null;
      const widthValue = +this.activeWidthEntry?.[0] || null;

      const toggleOptions = (options, shouldHide) => {
        options.forEach((option) => {
          const value = +option.dataset.value;
          const isOverLimit = value > 40;
          option.classList.toggle("disabled", isOverLimit && shouldHide);
        });
      };

      if (lengthValue !== null) {
        toggleOptions(widthOptions, lengthValue > 40);
      }

      if (widthValue !== null) {
        toggleOptions(lengthOptions, widthValue > 40);
      }
    }

    initCustomSelectListener() {
      this.addEventListener('custom-select:inited', (e) => {
        const { instance, reinit } = e.detail;

        // Catch any variant change that happened before this dropdown initialised.
        const variantChanged = this.syncVariantFromForm();

        if (instance.id === 'custom-product-configurator-length') {
          this.selects.length = { instance, reinit };
          this.initSelect(instance, reinit, 'length');
          if (variantChanged) this.handleVariantChange();

          instance.addEventListener('change', (event) => {
            const selectedOption = event.target.querySelector("li[aria-selected='true']");
            if (!selectedOption) return;
            const selectedOptionPrice = +selectedOption.dataset.price;
            const selectedOptionValue = selectedOption.dataset.value;
            this.activeLengthEntry = [selectedOptionValue, selectedOptionPrice];
            this.handleVariantChange();
          });
        } else if (instance.id === 'custom-product-configurator-width') {
          this.selects.width = { instance, reinit };
          this.initSelect(instance, reinit, 'width');
          if (variantChanged) this.handleVariantChange();

          instance.addEventListener('change', (event) => {
            const selectedOption = event.target.querySelector("li[aria-selected='true']");
            if (!selectedOption) return;
            const selectedOptionPrice = +selectedOption.dataset.price;
            const selectedOptionValue = selectedOption.dataset.value;
            this.activeWidthEntry = [selectedOptionValue, selectedOptionPrice];
            this.handleVariantChange();
          });
        }
      });
    }

    initSelect(instance, reinit, type) {
      if (!this.selectedVariantId || typeof +this.selectedVariantId !== 'number') return;
      const selectedVariant = this.variants.find(v => v.id === +this.selectedVariantId);
      if (!selectedVariant) return;

      const entries = Object.entries(selectedVariant.custom_pricing.pricingUpcharges || {});

      let newEntry = null;
      if (type && type === 'length' && this.activeLengthEntry && this.activeLengthEntry[0]) {
        newEntry = entries.find(e => e[0] === this.activeLengthEntry[0]);
        this.activeLengthEntry = newEntry;
      } else if (type && type === 'width' && this.activeWidthEntry && this.activeWidthEntry[0]) {
        newEntry = entries.find(e => e[0] === this.activeWidthEntry[0]);
        this.activeWidthEntry = newEntry;
      }

      this.renderOptionsToSelect(instance, entries, newEntry, type);
      reinit();
    }

    renderOptionsToSelect(instance, options, newEntry = null, type) {
      const listbox = instance.querySelector('.custom-select__listbox');
      if (!listbox) return;
      listbox.innerHTML = '';

      const idBase = instance.id || 'custom-select';
      const placeholder = document.createElement('li');
      placeholder.className = 'custom-select__option custom-select__option--placeholder flex items-center js-option';
      placeholder.setAttribute('role', 'option');
      placeholder.setAttribute('aria-disabled', 'true');
      placeholder.setAttribute('data-value', '');
      placeholder.setAttribute('data-price', '0');
      placeholder.id = `${idBase}-opt-placeholder`;

      let defaultPlaceholderValue = 'Select value'
      if (type && type === 'length') {
        defaultPlaceholderValue = 'Select Length'
      } else if (type && type === 'width') {
        defaultPlaceholderValue = 'Select Width'
      }
      const spanPlaceholder = document.createElement('span');
      spanPlaceholder.className = 'pointer-events-none';
      spanPlaceholder.textContent = defaultPlaceholderValue;

      placeholder.appendChild(spanPlaceholder);
      listbox.appendChild(placeholder);

      options.forEach((value, index) => {
        const isActiveOption = newEntry && newEntry[0] === value[0];
        const option = document.createElement('li');
        const optionTextContent = `<span class="custom-select__option--name">${value[0]} Inch</span> <span class="custom-select__option--price">+ $${value[1].toFixed(2)}</span>`;
        option.className = 'custom-select__option flex items-center js-option';
        option.setAttribute('role', 'option');
        option.setAttribute('data-value', value[0]);
        option.setAttribute('data-price', value[1].toFixed(2));
        option.id = `${idBase}-opt-${index}`;

        if (isActiveOption) {
          const textStart = instance.querySelector('.custom-select__btn .text-start');
          option.setAttribute('aria-selected', 'true');
          textStart.innerHTML = optionTextContent;
        }

        const span = document.createElement('span');
        span.className = 'pointer-events-none custom-select__option-item';
        span.innerHTML = optionTextContent

        option.appendChild(span);
        listbox.appendChild(option);
      });

      instance.options = instance.querySelectorAll('.custom-select__option');
    }
  }

  customElements.define('custom-product-configurator', CustomProductConfigurator);
}
